import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Pencil, Crown, Search, UserMinus, UserCheck2, Shuffle, X, Upload, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { usePageExport } from "@/context/ExportContext";
import { canManageTarget, ROLE_LABELS } from "@/lib/roles";
import UserDialog from "@/components/settings/UserDialog";
import UserDeleteDialog from "@/components/settings/UserDeleteDialog";
import UserPermissionsDialog from "@/components/settings/UserPermissionsDialog";
import BulkUserActionDialog from "@/components/settings/BulkUserActionDialog";
import BulkInviteUsersDialog from "@/components/settings/BulkInviteUsersDialog";

export default function UsersTable() {
  const { user: caller, hasPerm } = useAuth();
  const { activeSlug } = useClient();
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [showInactive, setShowInactive] = useState(true);
  const [dialogUser, setDialogUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [permsUser, setPermsUser] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null); // "deactivate" | "reactivate" | "update_role"
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);

  const canInvite = hasPerm("manage_users");
  const canManagePerms = hasPerm("users.manage_permissions");

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); setSelectedIds(new Set()); }, [activeSlug]);   // eslint-disable-line

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (!showInactive && !u.active) return false;
      if (!q) return true;
      return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    });
  }, [users, query, showInactive]);

  // Manageable rows in the *currently visible* filter set (used by select-all + bulk actions).
  const manageableVisible = useMemo(
    () => filtered.filter((u) => canManageTarget(caller, u)),
    [filtered, caller]
  );
  const selectedUsers = useMemo(
    () => users.filter((u) => selectedIds.has(u.id)),
    [users, selectedIds]
  );
  const allVisibleSelected = manageableVisible.length > 0
    && manageableVisible.every((u) => selectedIds.has(u.id));
  const anyInactiveSelected = selectedUsers.some((u) => !u.active);
  const anyActiveSelected = selectedUsers.some((u) => u.active);

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        manageableVisible.forEach((u) => next.delete(u.id));
      } else {
        manageableVisible.forEach((u) => next.add(u.id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const onBulkDone = () => { clearSelection(); load(); };

  const exportColumns = useMemo(() => [
    { key: "name", header: "Name" }, { key: "email", header: "Email" }, { key: "role", header: "Role" },
    { key: "is_super_admin", header: "Super Admin" }, { key: "active", header: "Active" },
    { key: "created_at", header: "Created" }, { key: "last_login_at", header: "Last Login" },
  ], []);
  usePageExport({ filename: "kippu-users.csv", rows: filtered, columns: exportColumns, label: "Users" });

  const openEdit = (u) => { setDialogUser(u); setDialogOpen(true); };
  const openInvite = () => { setDialogUser(null); setDialogOpen(true); };

  const toggleActive = async (u) => {
    const next = !u.active;
    // optimistic
    setUsers((arr) => arr.map((x) => x.id === u.id ? { ...x, active: next } : x));
    try {
      await api.patch(`/users/${u.id}`, { active: next });
      toast.success(next ? `${u.name} re-activated` : `${u.name} deactivated`);
    } catch (e) {
      setUsers((arr) => arr.map((x) => x.id === u.id ? { ...x, active: u.active } : x));
      toast.error(e?.response?.data?.detail || "Could not update status");
    }
  };

  return (
    <Card className="border-slate-200" data-testid="users-table-card">
      <CardHeader className="flex flex-row items-center justify-between pb-3 gap-3 flex-wrap">
        {selectedIds.size > 0 ? (
          /* Bulk action toolbar — shown when any row is selected */
          <div className="w-full flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-action-bar">
            <div className="flex items-center gap-2.5">
              <span className="bg-indigo-100 text-indigo-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                {selectedIds.size} selected
              </span>
              <button onClick={clearSelection} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1" data-testid="bulk-clear">
                <X className="w-3 h-3" /> Clear
              </button>
            </div>
            <div className="flex items-center gap-2">
              {anyActiveSelected && (
                <Button size="sm" variant="outline" onClick={() => setBulkAction("deactivate")}
                        className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="bulk-deactivate-btn">
                  <UserMinus className="w-3.5 h-3.5 mr-1.5" /> Deactivate
                </Button>
              )}
              {anyInactiveSelected && (
                <Button size="sm" variant="outline" onClick={() => setBulkAction("reactivate")}
                        className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="bulk-reactivate-btn">
                  <UserCheck2 className="w-3.5 h-3.5 mr-1.5" /> Reactivate
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setBulkAction("update_role")} data-testid="bulk-role-btn">
                <Shuffle className="w-3.5 h-3.5 mr-1.5" /> Change role
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <CardTitle className="font-heading text-base">Team members</CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">{filtered.length} of {users.length} users</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name or email"
                  className="pl-8 h-9 w-56 text-xs"
                  data-testid="users-search"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="show-inactive-toggle" />
                Show inactive
              </label>
              {canInvite && (
                <Button size="sm" variant="outline" onClick={() => setBulkInviteOpen(true)} data-testid="bulk-invite-btn">
                  <Upload className="w-4 h-4 mr-1.5" /> Bulk invite
                </Button>
              )}
              {canInvite && (
                <Button size="sm" onClick={openInvite} className="bg-indigo-600 hover:bg-indigo-700" data-testid="invite-user-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Invite user
                </Button>
              )}
            </div>
          </>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="text-xs uppercase tracking-wider text-slate-500">
              {canInvite && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={manageableVisible.length > 0 && allVisibleSelected}
                    onCheckedChange={toggleAllVisible}
                    disabled={manageableVisible.length === 0}
                    aria-label="Select all visible"
                    data-testid="select-all"
                  />
                </TableHead>
              )}
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last login</TableHead>
              {(canInvite || canManagePerms) && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={(canInvite || canManagePerms) ? 7 : 5} className="text-center text-slate-400 text-sm py-12">
                {users.length === 0 ? "No team members yet" : "No users match your filters"}
              </TableCell></TableRow>
            )}
            {filtered.map((u) => {
              const manageable = canManageTarget(caller, u);
              const isSelected = selectedIds.has(u.id);
              return (
                <TableRow key={u.id} className={`hover:bg-slate-50 ${!u.active ? "opacity-60" : ""} ${isSelected ? "bg-indigo-50/40" : ""}`} data-testid={`user-row-${u.email}`}>
                  {canInvite && (
                    <TableCell className="w-10">
                      {manageable ? (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(u.id)}
                          aria-label={`Select ${u.name}`}
                          data-testid={`row-checkbox-${u.email}`}
                        />
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="font-medium text-sm text-slate-800">
                    <div className="flex items-center gap-1.5">
                      {u.name}
                      {u.is_super_admin && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 font-mono text-xs">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs capitalize bg-slate-50">
                      {ROLE_LABELS[u.role] || u.role.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {manageable ? (
                      <button
                        type="button"
                        onClick={() => toggleActive(u)}
                        className={`text-xs px-2 py-0.5 rounded-full border transition ${u.active
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                          : "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200"}`}
                        data-testid={`status-toggle-${u.email}`}
                        title={u.active ? "Click to deactivate" : "Click to reactivate"}
                      >
                        {u.active ? "Active" : "Inactive"}
                      </button>
                    ) : (
                      <Badge variant="outline" className={`text-xs ${u.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500"}`}>
                        {u.active ? "Active" : "Inactive"}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "Never"}
                  </TableCell>
                  {(canInvite || canManagePerms) && (
                    <TableCell className="text-right">
                      {(manageable || (canManagePerms && !u.is_super_admin)) ? (
                        <div className="flex items-center justify-end gap-1">
                          {canManagePerms && !u.is_super_admin && (
                            <Button variant="ghost" size="sm" onClick={() => setPermsUser(u)} className="h-8 px-2 text-indigo-600 hover:bg-indigo-50" data-testid={`perms-user-${u.email}`} title="Manage permissions">
                              <ShieldCheck className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {manageable && canInvite && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="h-8 px-2 text-slate-600 hover:bg-slate-100" data-testid={`edit-user-${u.email}`}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setDeleteUser(u)} className="h-8 px-2 text-rose-600 hover:bg-rose-50" data-testid={`delete-user-${u.email}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>

      <UserDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setDialogUser(null); }}
        user={dialogUser}
        onSaved={load}
      />
      <UserDeleteDialog
        open={!!deleteUser}
        onOpenChange={(v) => { if (!v) setDeleteUser(null); }}
        user={deleteUser}
        onDeleted={load}
      />
      <UserPermissionsDialog
        open={!!permsUser}
        onOpenChange={(v) => { if (!v) setPermsUser(null); }}
        user={permsUser}
      />
      <BulkUserActionDialog
        open={!!bulkAction}
        onOpenChange={(v) => { if (!v) setBulkAction(null); }}
        action={bulkAction}
        userIds={Array.from(selectedIds)}
        onDone={onBulkDone}
      />
      <BulkInviteUsersDialog
        open={bulkInviteOpen}
        onOpenChange={setBulkInviteOpen}
        onImported={load}
      />
    </Card>
  );
}
